import type { UpgradeId } from "@/game/entities/config/upgrades";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";

export type UpgradeRetrograntItem = {
	upgradeId: UpgradeId;
	// Grant when settlements.mainLevel is at least this value
	unlockLevel: number;
	// Target upgrade level to set when eligible. Defaults to 1. Never lowers an existing level
	toLevel?: number;
};

export type UpgradeRetrogrant = {
	// Stable unique id, once applied for an account - it never runs again
	id: string;
	grants: Array<UpgradeRetrograntItem>;
};

/**
 * Append new entries here for future one-shot free upgrade grants.
 * Each id must stay unique and stable after release.
 *
 * town_privileges_workforce_rebalance_v1 QA / UAT:
 * - mainLevel < 45: no grants, id marked applied & second load no-ops
 * - mainLevel 45–59: only 111 at 1
 * - mainLevel 60–74: 111 + 114 at 1
 * - mainLevel >= 75: 111 + 114 + 115 at 1
 * - already owned: stay owned, missing siblings granted by tier
 * - mainLevel 44 that later reaches 45: still pays gems
 */
export const UPGRADE_RETROGRANTS: Array<UpgradeRetrogrant> = [
	{
		id: "town_privileges_workforce_rebalance_v1",
		grants: [
			{ upgradeId: UpgradeIds.town_privileges, unlockLevel: 45 },
			{ upgradeId: UpgradeIds.town_privileges2, unlockLevel: 60 },
			{ upgradeId: UpgradeIds.town_privileges3, unlockLevel: 75 },
		],
	},
];

// Applies pending one-shot upgrade retrogrants for the current account
// Persists via the normal post-load save path
export const applyUpgradeRetrogrants = (): void => {
	const userStore = useUserStore();
	const applied = new Set(userStore.appliedUpgradeRetrogrants);
	const pending: Array<UpgradeRetrogrant> = UPGRADE_RETROGRANTS.filter(
		(retrogrant: UpgradeRetrogrant): boolean => !applied.has(retrogrant.id),
	);
	if (!pending.length) return;

	const upgradesStore = useUpgradesStore();
	const mainLevel = useSettlementsStore().mainLevel;

	for (const retrogrant of pending) {
		for (const grant of retrogrant.grants) {
			const toLevel = grant.toLevel ?? 1;
			if (
				mainLevel >= grant.unlockLevel &&
				upgradesStore.getCurrentLevelById(grant.upgradeId) < toLevel
			) {
				upgradesStore.setUpgradeLevelById(grant.upgradeId, toLevel);
			}
		}
		userStore.markUpgradeRetrograntApplied(retrogrant.id);
	}
};
